import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

// =============================================================================
// Approval workflow (svc-07, via gateway /v1/approvals + /v1/approval-steps).
// Mirrors packages/shared-types/src/svc-07-approval-workflow/v1/*.schema.ts.
// =============================================================================

export type FlowType = "engineer" | "sales";
export type ApprovalDecision = "approve" | "reject";

export interface ApprovalAssignment {
  assignmentId: string;
  taskId: string;
  stepId: string;
  stepOrder: number;
  stepName: string;
  requiredRoleCode: string | null;
  assigneeUserId: string | null;
  assignedAt: string;
  decidedAt: string | null;
  decision: "approve" | "reject" | "auto_approve" | null;
  decisionComment: string | null;
}

export interface ApprovalAuditEntry {
  id: string;
  taskId: string;
  flowType: FlowType;
  eventType: string;
  fromStatus: string | null;
  toStatus: string;
  stepName: string | null;
  stepOrder: number | null;
  actorUserId: string | null;
  actorEmail: string;
  actorName: string | null;
  actorRoleCode: string | null;
  decisionReason: string | null;
  occurredAt: string;
}

export interface ApprovalState {
  taskId: string;
  flowType: FlowType;
  complete: boolean;
  rejected: boolean;
  currentAssignment: ApprovalAssignment | null;
  assignments: ApprovalAssignment[];
  audit: ApprovalAuditEntry[];
}

export interface ApprovalQueueItem {
  taskId: string;
  flowType: FlowType;
  stepId: string;
  stepOrder: number;
  stepName: string;
  assignmentId: string;
  assignedAt: string;
  taskCreatedBy: string | null;
  taskCreatedAt: string | null;
}

export interface PaginatedApprovalQueue {
  items: ApprovalQueueItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ApprovalDecisionInput {
  decision: ApprovalDecision;
  comment?: string | null;
}

export interface ReassignInput {
  newAssigneeUserId: string;
  comment?: string | null;
}

// --- Approval steps (admin config) -----------------------------------------

export interface ApprovalStep {
  stepId: string;
  flowType: FlowType;
  stepOrder: number;
  stepName: string;
  requiredRoleId: number;
  requiredRoleCode: string | null;
  autoApproveOwn: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateApprovalStepInput {
  flowType: FlowType;
  stepOrder: number;
  stepName: string;
  requiredRoleId: number;
  autoApproveOwn?: boolean;
  isActive?: boolean;
}

function toQuery(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** GET /v1/approvals/queue?assignee=me — the caller's pending approvals. */
export async function getApprovalQueue(
  query: { flowType?: FlowType; limit?: number; offset?: number } = {},
): Promise<PaginatedApprovalQueue> {
  const res = await Api.get(
    `/v1/approvals/queue${toQuery({ assignee: "me", ...query })}`,
  );
  return unwrap<PaginatedApprovalQueue>(res.data);
}

/** GET /v1/approvals/tasks/:taskId — full approval state for a task. */
export async function getApprovalState(taskId: string): Promise<ApprovalState> {
  const res = await Api.get(`/v1/approvals/tasks/${encodeURIComponent(taskId)}`);
  return unwrap<ApprovalState>(res.data);
}

/** POST /v1/approvals/tasks/:taskId/decide. */
export async function decideApproval(
  taskId: string,
  body: ApprovalDecisionInput,
): Promise<ApprovalState> {
  const res = await Api.post(
    `/v1/approvals/tasks/${encodeURIComponent(taskId)}/decide`,
    body,
  );
  return unwrap<ApprovalState>(res.data);
}

/** POST /v1/approvals/tasks/:taskId/reassign. */
export async function reassignApproval(
  taskId: string,
  body: ReassignInput,
): Promise<ApprovalState> {
  const res = await Api.post(
    `/v1/approvals/tasks/${encodeURIComponent(taskId)}/reassign`,
    body,
  );
  return unwrap<ApprovalState>(res.data);
}

/** GET /v1/approval-steps — the data-driven step config. */
export async function listApprovalSteps(): Promise<ApprovalStep[]> {
  const res = await Api.get(`/v1/approval-steps`);
  return unwrap<{ items: ApprovalStep[] }>(res.data).items;
}

/** PUT /v1/approval-steps — replace the full step config (admin). */
export async function updateApprovalSteps(
  items: UpdateApprovalStepInput[],
): Promise<ApprovalStep[]> {
  const res = await Api.put(`/v1/approval-steps`, { items });
  return unwrap<{ items: ApprovalStep[] }>(res.data).items;
}
